/**
 * FLEET MONITORING — the compact fleet list (approved mockup
 * docs/mockups/fleet-monitoring-redesign-mockup.html, screen "1 · Fleet list").
 *
 * One row per till, six facts per row: what it is called, what it runs, whether
 * an update is coming, what it still holds unsent, what it has reported, and
 * when it last checked in. The whole row opens the device page — there is no
 * side drawer and no per-row action menu here; every action lives on the
 * device page.
 *
 * Every word about a till comes from `fleetStatus.ts` (shared with the device
 * page) and every row from `buildFleetRows` (shared with FleetTable), so the
 * list, the device page and POS Config can never disagree about one device.
 *
 * `/pos-config` keeps the full FleetTable; this list is only rendered by
 * BrandingTab when `layout="monitoring"`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Monitor, RefreshCw, Search, X } from 'lucide-react';
import type { DeviceHealthDeviceStat, DeviceHealthProblem } from '@platform/shared-types';
import ShimmerImage from '../../components/ShimmerImage';
import { RubyGemMark } from '../../components/RubyGemMark';
import { type DeviceModelRow } from '../../services/hardwareApi';
import { deviceHealthApi } from '../../services/deviceHealthApi';
import {
  buildFleetRows,
  type FleetDeviceRecord,
  type FleetRow,
  type FleetUnitRecord,
  type FleetRegisterLinkMap,
  type ResolvedTargetMap,
} from '../pos/fleet/fleetRows';
import { pageWindow } from '../pos/branding/FleetTable';
import { deviceHref, isOnline, modelImage, relativeTime, unitHref } from '../pos/device/deviceDetail';
import { FLEET_MONITORING_DEVICE_BASE } from '../pos/device/deviceLinkBase';
import {
  deviceDisplayName,
  ISSUE_STATUS_LABEL,
  ISSUE_STATUS_TONE,
  mostUrgent,
  openIssuesByDevice,
  TONE_PILL,
  updateStatus,
  uploadsWaiting,
} from './fleetStatus';

const PAGE_SIZE = 50;
const ISSUES_POLL_MS = 60_000;

/** Wide layout: the seven columns of the mockup. Below 1100px each row stacks. */
const WIDE_GRID = 'min-[1100px]:grid-cols-[minmax(240px,2.2fr)_1.3fr_1.5fr_0.9fr_1fr_1fr_28px]';

export type FleetFilterKey = 'all' | 'attention' | 'queued' | 'behind' | 'issues' | 'uploads' | 'pinned' | 'offline';

const QUEUED_STATES = new Set(['downloading', 'ready', 'waiting_for_idle', 'installing']);

export interface FleetListProps {
  deviceType: string;
  devices: FleetDeviceRecord[];
  units?: FleetUnitRecord[];
  models?: DeviceModelRow[];
  resolvedTargets?: ResolvedTargetMap;
  targetsUnavailable?: boolean;
  targetsTruncated?: boolean;
  devicesError?: string | null;
  devicesTruncated?: boolean;
  onRetry?: () => void;
  registerLinks?: FleetRegisterLinkMap;
  registerUnresolved?: string[];
  tenantNames?: Record<string, string>;
  orders?: Record<string, { orderNo: string; status: string }>;
  enrichmentError?: string | null;
  /** deviceId → last-24h stats from DeviceHealthPanel. Absent = unknown, never 0. */
  health?: Record<string, DeviceHealthDeviceStat>;
  /** Accepted for parity with FleetTable; this list has no write actions. */
  onChanged?: () => void;
  registerMissing?: string[];
}

export default function FleetList({
  deviceType,
  devices,
  units = [],
  models = [],
  resolvedTargets = {},
  targetsUnavailable = false,
  targetsTruncated = false,
  devicesError = null,
  devicesTruncated = false,
  onRetry,
  registerLinks = {},
  registerUnresolved = [],
  tenantNames = {},
  orders = {},
  enrichmentError = null,
  health,
}: FleetListProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── open issues: one fleet-wide read on mount, then every 60s ──
  // `null` = not read yet. A failed read sets `issuesError` and the column says
  // "Issues unavailable" — never 0, which would read as "nothing reported".
  const [issues, setIssues] = useState<Map<string, DeviceHealthProblem[]> | null>(null);
  const [issuesError, setIssuesError] = useState(false);
  useEffect(() => {
    let live = true;
    const load = () => {
      deviceHealthApi.problems({ status: 'open', window: '7d' })
        .then((p) => { if (!live) return; setIssues(openIssuesByDevice(p || [])); setIssuesError(false); })
        .catch(() => { if (live) setIssuesError(true); });
    };
    load();
    const t = setInterval(load, ISSUES_POLL_MS);
    return () => { live = false; clearInterval(t); };
  }, []);

  const modelById = useMemo(() => new Map(models.map((m) => [m.modelId, m])), [models]);

  // The SAME merge FleetTable uses — release status is never re-derived here.
  const rows = useMemo(
    () => buildFleetRows(devices, units, resolvedTargets, { deviceType }, registerLinks),
    [devices, units, resolvedTargets, deviceType, registerLinks],
  );

  // The old `?unitId=` deep link opened the support drawer. The drawer is gone;
  // a unit that has a device goes straight to that device's page.
  useEffect(() => {
    const wanted = searchParams.get('unitId');
    if (!wanted) return;
    const match = rows.find((r) => r.unitId === wanted && r.deviceId);
    if (match?.deviceId) {
      navigate(deviceHref(match.deviceId, undefined, match.deviceType || deviceType, FLEET_MONITORING_DEVICE_BASE), { replace: true });
    }
  }, [rows, searchParams, navigate, deviceType]);

  const openFor = (r: FleetRow): DeviceHealthProblem[] => (r.deviceId && issues?.get(r.deviceId)) || [];
  const issuesKnown = !!issues && !issuesError;

  const needsAttention = (r: FleetRow): boolean => {
    const up = uploadsWaiting(r);
    if (r.releaseStatus === 'stale' && up && up.total > 0) return true;
    if (r.updateState === 'failed') return true;
    if (r.bootInstalled === false && !!r.error) return true;
    return issuesKnown && openFor(r).some((p) => (p.ticketStatus ?? 'new') === 'new');
  };

  const FILTERS: { key: FleetFilterKey; label: string; test: (r: FleetRow) => boolean; issueBased?: boolean }[] = [
    { key: 'all', label: 'All', test: () => true },
    { key: 'attention', label: 'Needs attention', test: needsAttention },
    { key: 'queued', label: 'Update queued', test: (r) => !!r.updateState && QUEUED_STATES.has(r.updateState) },
    { key: 'behind', label: 'Behind', test: (r) => r.drift },
    { key: 'issues', label: 'Open issues', test: (r) => openFor(r).length > 0, issueBased: true },
    { key: 'uploads', label: 'Uploads waiting', test: (r) => (uploadsWaiting(r)?.total ?? 0) > 0 },
    { key: 'pinned', label: 'Pinned', test: (r) => r.targetSource === 'pin' },
    { key: 'offline', label: 'Offline', test: (r) => !isOnline(r) },
  ];

  const fParam = searchParams.get('f') as FleetFilterKey | null;
  const filter = FILTERS.find((x) => x.key === fParam) ?? FILTERS[0];

  const updateParams = (mut: (p: URLSearchParams) => void) =>
    setSearchParams((prev) => { const p = new URLSearchParams(prev); mut(p); return p; }, { replace: true });

  const setFilter = (k: FleetFilterKey) => updateParams((p) => {
    if (k === 'all') p.delete('f'); else p.set('f', k);
    p.delete('page');
  });

  // ── search: the same six fields as FleetTable's matchesQuery ──
  const q = searchParams.get('q') || '';
  const qLower = q.trim().toLowerCase();
  const setQuery = (next: string) => updateParams((p) => {
    if (next) p.set('q', next); else p.delete('q');
    p.delete('page');
  });
  const unresolvedIds = useMemo(() => new Set(registerUnresolved), [registerUnresolved]);
  const isUnresolved = (r: FleetRow) => !!r.deviceId && unresolvedIds.has(r.deviceId);
  const bizName = (r: FleetRow) => (r.tenantId ? tenantNames[r.tenantId] : undefined);
  const matchesQuery = (r: FleetRow): boolean => {
    if (!qLower) return true;
    const fields: (string | undefined)[] = [r.registerName ?? undefined, r.trackingId, r.serialNumber];
    if (!isUnresolved(r)) {
      const orderNo = r.orderId ? orders[r.orderId]?.orderNo : undefined;
      fields.push(bizName(r), r.provisionCode, orderNo);
    }
    return fields.some((f) => !!f && f.toLowerCase().includes(qLower));
  };

  const visible = rows
    .filter(filter.test)
    .filter(matchesQuery)
    .map((r) => ({ r, attn: needsAttention(r) }))
    .sort((a, b) => {
      if (a.attn !== b.attn) return a.attn ? -1 : 1;
      return (b.r.updatedAt ?? -1) - (a.r.updatedAt ?? -1);
    });

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Math.min(Math.max(1, Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1), totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const shown = visible.slice(pageStart, pageStart + PAGE_SIZE);
  const setPage = (n: number) => updateParams((p) => { if (n <= 1) p.delete('page'); else p.set('page', String(n)); });

  function emptyState(): string {
    if (rows.length === 0) {
      return devicesError
        ? 'Device check-ins could not be loaded — the fleet is unknown, not empty.'
        : 'No devices have checked in yet.';
    }
    if (filter.issueBased && !issuesKnown) return 'Open issues could not be read, so this filter cannot be applied right now.';
    return 'No devices match. Clear the search or pick another filter.';
  }

  const unresolvedInScope = qLower ? rows.filter(filter.test).filter(isUnresolved).length : 0;

  return (
    <div className="space-y-3">
      {/* ── honest read states, compact ── */}
      {devicesError && (
        <Banner tone="bad" role="alert">
          <span>
            <b>Could not load device check-ins — this list is NOT showing the fleet.</b>{' '}
            An empty list means the read failed, not that no device has checked in.{' '}
            <span className="opacity-80">{devicesError}</span>
          </span>
          {onRetry && <RetryButton onClick={onRetry} />}
        </Banner>
      )}
      {(devicesTruncated || targetsTruncated) && (
        <Banner tone="warn">
          <span>
            {devicesTruncated
              ? 'The server returned only part of this fleet — devices past the cut are missing from the list and every count.'
              : 'Targets were resolved for only part of this fleet — devices past the cut show no target, not "on target".'}
            {' '}A partial view, not evidence that anything is wrong with a device.
          </span>
        </Banner>
      )}
      {targetsUnavailable && (
        <Banner tone="warn">
          <span>Resolved targets unavailable — no device is shown as on target on missing evidence. Every other column is live.</span>
        </Banner>
      )}
      {enrichmentError && (
        <Banner tone="warn">
          <span>{enrichmentError} Every other column is live — this is a read that did not return, not a device fault.</span>
          {onRetry && <RetryButton onClick={onRetry} />}
        </Banner>
      )}

      {/* ── sticky toolbar: one search box + filter chips ── */}
      <div className="sticky top-0 z-10 -mx-1 space-y-2.5 bg-stone-100 px-1 py-1">
        <label className="flex min-h-[44px] items-center gap-2 rounded-[10px] border border-stone-300 bg-white px-3 focus-within:border-[#DC143C] focus-within:ring-2 focus-within:ring-[#FFF1F3]">
          <Search size={15} className="shrink-0 text-stone-400" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search business, register, provisioning code, order, tracking id, serial…"
            aria-label="Search fleet"
            autoComplete="off"
            className="min-w-0 flex-1 border-0 bg-transparent text-[14px] text-stone-900 outline-none placeholder:text-stone-400"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear the search"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            >
              <X size={14} />
            </button>
          )}
        </label>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter">
          {FILTERS.map((f) => {
            const active = f.key === filter.key;
            const count = f.issueBased && !issuesKnown ? '—' : String(rows.filter(f.test).length);
            const attnIdle = f.key === 'attention' && !active;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f.key)}
                className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3 text-[13px] ${
                  active
                    ? 'border-[#DC143C] bg-[#DC143C] font-semibold text-white'
                    : attnIdle
                      ? 'border-red-300 bg-white text-red-700 hover:bg-[#FFF1F3]'
                      : 'border-stone-300 bg-white text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C]'
                }`}
              >
                {f.label}
                <span className={`tabular-nums ${active ? 'text-[#FFE4E8]' : 'text-stone-500'}`} data-testid={`chip-count-${f.key}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {unresolvedInScope > 0 && (
        <Banner tone="warn">
          <span>
            {unresolvedInScope} device{unresolvedInScope === 1 ? ' was' : 's were'} searched on fewer fields — their register
            lookup failed, so business name, provisioning code and order number could not be matched.
          </span>
        </Banner>
      )}

      {/* ── the list ── */}
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white" role="region" aria-label="Devices">
        <div className={`hidden min-h-[38px] items-center gap-3 border-b border-stone-200 bg-stone-50 px-4 text-[11px] font-semibold uppercase tracking-wider text-stone-500 min-[1100px]:grid ${WIDE_GRID}`}>
          <span>Device</span><span>Version</span><span>Update</span><span>Uploads waiting</span><span>Issues</span><span>Last check-in</span><span />
        </div>

        {shown.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-stone-500">{emptyState()}</div>
        ) : (
          <ul>
            {shown.map(({ r, attn }) => (
              <li key={r.key} className="border-b border-stone-200 last:border-b-0">
                <FleetListRow
                  row={r}
                  attention={attn}
                  model={r.modelId ? modelById.get(r.modelId) : undefined}
                  tenantName={bizName(r)}
                  unresolved={isUnresolved(r)}
                  issues={openFor(r)}
                  issuesState={issuesError ? 'error' : issues ? 'ok' : 'loading'}
                  health={r.deviceId ? health?.[r.deviceId] : undefined}
                  targetsUnavailable={targetsUnavailable}
                  deviceType={deviceType}
                />
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-stone-200 bg-stone-50 px-4 py-2.5 text-[12.5px] text-stone-500">
          <span>
            Showing <b className="text-stone-900">{visible.length === 0 ? 0 : shown.length}</b> of{' '}
            <b className="text-stone-900">{visible.length}</b> device{visible.length === 1 ? '' : 's'}
            {visible.length !== rows.length && <> (fleet {rows.length})</>} · refreshes every 15s
          </span>
          {totalPages > 1 && (
            <nav className="flex flex-wrap items-center gap-1" aria-label="Pages">
              <PageButton label="‹" ariaLabel="Previous page" disabled={page <= 1} onClick={() => setPage(page - 1)} />
              {pageWindow(page, totalPages).map((p, i) => (p < 0 ? (
                <span key={`gap${i}`} aria-hidden className="px-1 text-stone-300">…</span>
              ) : (
                <PageButton key={p} label={String(p)} current={p === page} onClick={() => setPage(p)} />
              )))}
              <PageButton label="›" ariaLabel="Next page" disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const DOT_ON = 'bg-green-500 ring-[3px] ring-green-100';
const DOT_RECENT = 'bg-amber-500';
const DOT_OFF = 'bg-stone-400';
const TWO_HOURS = 2 * 60 * 60 * 1000;

function FleetListRow({
  row, attention, model, tenantName, unresolved, issues, issuesState, health, targetsUnavailable, deviceType,
}: {
  row: FleetRow;
  attention: boolean;
  model?: DeviceModelRow;
  tenantName?: string;
  unresolved: boolean;
  issues: DeviceHealthProblem[];
  issuesState: 'loading' | 'ok' | 'error';
  health?: DeviceHealthDeviceStat;
  targetsUnavailable: boolean;
  deviceType: string;
}) {
  const name = deviceDisplayName(row);
  const modelName = model?.modelName || row.hardwareModel;
  const meta = [
    tenantName || (unresolved ? 'business unavailable' : undefined),
    row.locationName || undefined,
    modelName,
  ].filter(Boolean).join(' · ');
  const upd = updateStatus(row);
  const up = uploadsWaiting(row);
  const seen = relativeTime(row.updatedAt);
  const online = isOnline(row);
  const neverCheckedIn = !row.deviceId;
  const dot = online ? DOT_ON : row.updatedAt && Date.now() - row.updatedAt < TWO_HOURS ? DOT_RECENT : DOT_OFF;

  let versionSub: string;
  if (!row.targetVersion) versionSub = targetsUnavailable ? 'target unavailable' : 'no target';
  else if (!row.drift) versionSub = 'on target';
  else versionSub = `target ${row.targetVersion}`;
  if (row.versionSource === 'install') versionSub = `last known from install · ${versionSub}`;

  const urgent = mostUrgent(issues);
  const errs = health ? `${health.errors24h} error${health.errors24h === 1 ? '' : 's'} 24h` : null;

  const cls = `relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 px-4 py-3 ${WIDE_GRID} min-[1100px]:min-h-[64px] min-[1100px]:py-2 ${
    neverCheckedIn ? '' : 'hover:bg-[#FFF8F9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#DC143C]'
  }`;

  const label = (t: string) => <span className="mr-1.5 text-[11px] uppercase tracking-wider text-stone-400 min-[1100px]:hidden">{t}</span>;

  const body = (
    <>
      {attention && (
        <span aria-hidden className="absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r bg-red-700" data-testid="attention-stripe" />
      )}
      {attention && <span className="sr-only">Needs attention.</span>}

      {/* Device */}
      <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-3 min-[1100px]:col-start-auto min-[1100px]:row-start-auto">
        <ShimmerImage
          src={modelImage(model)}
          alt={modelName || name}
          width={44}
          height={44}
          objectFit="contain"
          className="shrink-0 rounded-[10px] border border-stone-200"
          fallbackBgColor="bg-stone-100"
          fallbackIcon={<Monitor size={20} className="text-stone-400" />}
        />
        <div className="min-w-0">
          <div className="truncate font-semibold text-stone-900" data-testid="device-name">{name}</div>
          {meta && <div className="truncate text-[12px] text-stone-500" title={meta}>{meta}</div>}
        </div>
      </div>

      {/* Version */}
      <div className="col-span-2 min-w-0 min-[1100px]:col-span-1">
        {row.displayVersion
          ? <div className="truncate font-mono text-[13px] tabular-nums text-stone-900">{label('Version')}{row.displayVersion}</div>
          : <div className="text-[13px] text-stone-400">{label('Version')}—</div>}
        <div className="truncate text-[12px] text-stone-500">{versionSub}</div>
      </div>

      {/* Update */}
      <div className="col-span-2 min-w-0 min-[1100px]:col-span-1">
        <span className={`inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[upd.tone]}`}>{upd.label}</span>
        <div className="truncate text-[12px] text-stone-500" title={upd.detail}>{upd.detail}</div>
      </div>

      {/* The small facts: one wrapped line when stacked, three grid cells when wide. */}
      <div className="col-span-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 min-[1100px]:contents">
        {/* Uploads waiting */}
        <div className="min-w-0 text-[13px]">
          {label('Uploads')}
          {up === null
            ? <span className="text-stone-400">Not reported</span>
            : up.total === 0
              ? <span className="text-stone-500">None</span>
              : (
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[row.releaseStatus === 'stale' ? 'bad' : 'warn']}`}
                  title={`${up.sales} sale${up.sales === 1 ? '' : 's'} · ${up.other} other`}
                >
                  {up.sales > 0 ? `${up.sales} sale${up.sales === 1 ? '' : 's'}` : `${up.total} waiting`}
                  {up.sales > 0 && up.other > 0 ? ` +${up.other}` : ''}
                </span>
              )}
        </div>

        {/* Issues */}
        <div className="min-w-0 text-[13px]">
          {label('Issues')}
          {issuesState === 'error' ? (
            <span className="text-stone-400">Issues unavailable</span>
          ) : issuesState === 'loading' ? (
            <span className="inline-flex text-stone-400"><RubyGemMark size={14} drawing color="currentColor" /><span className="sr-only">Loading issues</span></span>
          ) : urgent ? (
            <>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[ISSUE_STATUS_TONE[urgent.ticketStatus ?? 'new'] ?? 'mute']}`}>
                {issues.length} open
              </span>
              <div className="truncate text-[12px] text-stone-500">
                {ISSUE_STATUS_LABEL[urgent.ticketStatus ?? 'new'] ?? urgent.ticketStatus}{errs ? ` · ${errs}` : ''}
              </div>
            </>
          ) : (
            <>
              <span className="text-stone-500">None open</span>
              {errs && <div className="truncate text-[12px] text-stone-500">{errs}</div>}
            </>
          )}
        </div>

        {/* Last check-in */}
        <div className="min-w-0 text-[13px]">
          {neverCheckedIn ? (
            <span className="inline-flex flex-wrap items-center gap-x-2">
              <span className="text-stone-500">Never checked in</span>
              {unitHref(row.unitId) && (
                <Link to={unitHref(row.unitId)!} className="inline-flex min-h-[44px] items-center font-semibold text-[#DC143C] hover:underline">
                  Open unit
                </Link>
              )}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <span aria-hidden className={`inline-block h-2 w-2 shrink-0 rounded-full ${dot}`} />
              <span className="text-stone-700">{seen ?? '—'}</span>
              {!online && <span className="sr-only">(offline)</span>}
            </span>
          )}
        </div>
      </div>

      {/* Chevron */}
      <span aria-hidden className="col-start-2 row-start-1 text-stone-300 min-[1100px]:col-start-auto min-[1100px]:row-start-auto">
        {!neverCheckedIn && <ChevronRight size={18} />}
      </span>
    </>
  );

  if (neverCheckedIn) return <div className={cls} data-testid="fleet-row">{body}</div>;
  return (
    <Link
      to={deviceHref(row.deviceId!, undefined, row.deviceType || deviceType, FLEET_MONITORING_DEVICE_BASE)}
      className={`${cls} text-inherit no-underline`}
      data-testid="fleet-row"
      aria-label={`${name}${meta ? `, ${meta}` : ''}`}
    >
      {body}
    </Link>
  );
}

function Banner({ tone, role = 'status', children }: { tone: 'bad' | 'warn'; role?: 'alert' | 'status'; children: React.ReactNode }) {
  const c = tone === 'bad'
    ? 'border-red-300 bg-red-50 text-red-800'
    : 'border-amber-300 bg-amber-50 text-amber-800';
  return (
    <div role={role} className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3 py-2 text-[12.5px] ${c}`}>
      <AlertTriangle size={14} className="shrink-0" aria-hidden />
      {children}
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-auto inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-stone-900 px-3 text-[12px] font-semibold text-white hover:bg-stone-700"
    >
      <RefreshCw size={13} /> Retry
    </button>
  );
}

function PageButton({ label, ariaLabel, current, disabled, onClick }: {
  label: string; ariaLabel?: string; current?: boolean; disabled?: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={current ? 'page' : undefined}
      className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-[12.5px] font-semibold disabled:opacity-40 ${
        current ? 'bg-[#DC143C] text-white' : 'border border-stone-300 bg-white text-stone-600 hover:border-[#DC143C] hover:text-[#DC143C]'
      }`}
    >
      {label}
    </button>
  );
}
